import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  submitProposal,
  submitMailingList,
  submitEnforcementTracker,
  type ProposalSubmission,
} from '@/lib/leads';

export const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.wedpo.com';

export type DiagAnswer = 'yes' | 'no' | 'unsure' | undefined;
export type PackageName = 'Basic' | 'Advanced' | 'Custom';
export type ModalKind = 'proposal' | 'ropa' | null;

export type Package = {
  tier: string;
  name: PackageName;
  desc: string;
  priceMonthly: number | null;
  ideal: string;
  featured: boolean;
  ctaLabel: string;
  ctaStyle: 'primary' | 'dark' | 'outline';
  features: string[];
};

export const PACKAGES: Package[] = [
  {
    tier: 'Tier 1',
    name: 'Basic',
    desc: 'Your compliance starting point - essential DPO coverage out of the box.',
    priceMonthly: 390,
    ideal: 'Ideal for: startups & SMBs',
    featured: false,
    ctaLabel: 'Get a proposal',
    ctaStyle: 'outline',
    features: [
      'DPO designation & official appointment letter',
      'Listed in privacy notices / website',
      'First-tier response to Data Subject Requests (DSRs)',
      'Initial GDPR compliance readiness audit',
      'Option for capped consultation hours (with discounted rates)',
      'Access to our compliance scanner tool',
    ],
  },
  {
    tier: 'Tier 2',
    name: 'Advanced',
    desc: 'Everything in Basic, plus hands-on compliance management for growing companies.',
    priceMonthly: 790,
    ideal: 'Ideal for: scale-up SaaS & mid-market',
    featured: true,
    ctaLabel: 'Get a proposal',
    ctaStyle: 'dark',
    features: [
      'All Basic features included',
      'ROPA guidance and tool',
      'Capped consultation hours',
      'Access to templates',
      'Access to our compliance scanner tool',
    ],
  },
  {
    tier: 'Tier 3',
    name: 'Custom',
    desc: "A fully tailored compliance programme, built around your organisation's complexity.",
    priceMonthly: null,
    ideal: 'Ideal for: enterprises & regulated sectors',
    featured: false,
    ctaLabel: 'Talk to an expert',
    ctaStyle: 'primary',
    features: [
      'All Advanced features included',
      'Dedicated or fractional DPO team',
      'Product privacy reviews (automated and manual)',
      'Risk Assessment Frameworks (DPIA, LIA, TIA)',
      'GDPR certification support',
      'Audit & regulator response support',
      'On-demand consultation, custom integrations',
      'Access to our privacy and AI automation platform',
    ],
  },
];

type Mark = 'yes' | 'no' | string;
export const COMPARISON: { section: string; rows: [string, Mark, Mark, Mark][] }[] = [
  {
    section: 'Core DPO services',
    rows: [
      ['DPO designation', 'yes', 'yes', 'yes'],
      ['Representation in privacy notices', 'yes', 'yes', 'yes'],
      ['First-tier DSR response', 'yes', 'yes', 'yes'],
      ['Official compliance approvals', 'no', 'Unsigned / verified', 'yes'],
      ['Audit & regulator response support', 'no', 'no', 'yes'],
    ],
  },
  {
    section: 'Compliance programme',
    rows: [
      ['Initial GDPR readiness audit', 'Limited', 'Full', 'Full + integrations'],
      ['Template & toolkit access', 'Limited', 'Full', 'Custom'],
      ['ROPA management', 'no', 'Guidance + tool', 'yes'],
      ['Compliance scanner tool', 'yes', 'yes', 'yes'],
      ['Vendor management toolkit', 'no', 'no', 'yes'],
      ['Policy center and templates', 'no', 'no', 'yes'],
    ],
  },
  {
    section: 'Consultation & advisory',
    rows: [
      ['Consultation hours', 'With fee', 'Capped', 'On-demand'],
      ['Regulatory updates & enforcement alerts', 'Monthly', 'yes', 'yes'],
      ['Staff training materials', 'no', 'no', 'yes'],
      ['Awareness training & Q&A sessions', 'no', 'General + Q&A', 'Custom'],
    ],
  },
  {
    section: 'Product privacy & risk',
    rows: [
      ['Product privacy reviews', 'no', 'Add-on', 'yes'],
      ['Risk assessment frameworks (DPIA, LIA, TIA)', 'no', 'no', 'yes'],
      ['GDPR certification support', 'no', 'no', 'yes'],
    ],
  },
  {
    section: 'Team & delivery',
    rows: [
      ['Named DPO contact', 'yes', 'yes', 'yes'],
      ['Dedicated / fractional DPO team', 'no', 'no', 'yes'],
    ],
  },
];

export const SOLUTION_OPTIONS = [
  'DPO as a Service',
  'CISO as a Service',
  'GDPR Full Toolkit',
  'GDPR Readiness Audit',
  'Policy Center',
  'Vendors Management Kit',
];

/* ─── Wordmark / Slash SVG ─────────────────────────────── */

let slashCounter = 0;
function nextSlashId() { slashCounter += 1; return `wedpo-slash-${slashCounter}`; }

export function Slash({ className }: { className?: string }) {
  // Each instance gets a unique gradient ID so multiple wordmarks coexist on the page.
  const idRef = useRef<string>();
  if (!idRef.current) idRef.current = nextSlashId();
  const id = idRef.current;
  return (
    <svg className={`slash ${className ?? ''}`} viewBox="0 0 12 22.08" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#7B3FE4" />
          <stop offset="50%" stopColor="#3E5CCF" />
          <stop offset="100%" stopColor="#3EC7D9" />
        </linearGradient>
      </defs>
      <line
        x1="1.32" y1="20.76" x2="10.68" y2="1.32"
        stroke={`url(#${id})`} strokeWidth="2.64" strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export function WordMark({ className, large }: { className?: string; large?: boolean }) {
  return (
    <span className={`mark ${large ? 'hero-mark' : ''} ${className ?? ''}`} aria-label="we/dpo">
      <span>we</span>
      <Slash />
      <span>dpo</span>
    </span>
  );
}

/* ─── Top strip ─────────────────────────────────────────── */
export function TopStrip() {
  return (
    <div className="top-strip">
      Powered by Entero Strategies · Compliance experts since 2013
    </div>
  );
}

/* ─── Nav ───────────────────────────────────────────────── */

export type NavActive =
  | 'home-why'
  | 'home-platform'
  | 'home-faq'
  | 'home-contact'
  | 'packages'
  | null;

export function Nav({
  active,
  onProposal,
  onLogin,
}: {
  active: NavActive;
  onProposal: () => void;
  onLogin?: () => void;
}) {
  const handleLogoClick = (e: React.MouseEvent) => {
    // Let modified clicks (new tab, etc.) behave normally.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (window.location.pathname === '/') {
      // Already on landing - strip any hash and scroll to the hero, even if
      // react-router would treat the click as a no-op.
      e.preventDefault();
      window.history.replaceState(null, '', '/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Crossing routes - let <Link> navigate, then jump to the top.
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 0);
    }
  };

  return (
    <nav>
      <Link className="nav-logo mark" to="/" onClick={handleLogoClick}>
        <span>we</span>
        <Slash />
        <span>dpo</span>
      </Link>
      <div className="nav-links">
        <Link to="/#why" className={active === 'home-why' ? 'nav-active' : undefined}>About</Link>
        <Link to="/packages#choose-your-package" className={active === 'packages' ? 'nav-active' : undefined}>Packages</Link>
        <a href="https://scanner.wedpo.com" target="_blank" rel="noopener noreferrer">Compliance Scanner</a>
        <Link to="/#platform" className={active === 'home-platform' ? 'nav-active' : undefined}>
          Platform <span className="nav-soon-pill">Coming Soon</span>
        </Link>
        <Link to="/#faq" className={active === 'home-faq' ? 'nav-active' : undefined}>FAQ</Link>
        <Link to="/#contact" className={active === 'home-contact' ? 'nav-active' : undefined}>Contact</Link>
        <span className="nav-divider" aria-hidden="true" />
        <a
          href={onLogin ? '#' : `${APP_URL}/login`}
          onClick={onLogin ? (e) => { e.preventDefault(); onLogin(); } : undefined}
          className="nav-auth"
        >
          Log in / Sign up
        </a>
        <a href="#" onClick={(e) => { e.preventDefault(); onProposal(); }} className="nav-cta">
          Get a Proposal
        </a>
      </div>
    </nav>
  );
}

/* ─── Footer (with mailing + tracker forms) ─────────────── */

export function FooterFull() {
  const [mailEmail, setMailEmail] = useState('');
  const [mailSent, setMailSent] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);
  const [trackerEmail, setTrackerEmail] = useState('');
  const [trackerSent, setTrackerSent] = useState(false);
  const [trackerBusy, setTrackerBusy] = useState(false);

  const handleMailing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mailEmail) return;
    setMailBusy(true);
    try {
      await submitMailingList(mailEmail);
      setMailSent(true);
      setMailEmail('');
    } catch (err) {
      console.error('mailing list submit failed', err);
    } finally {
      setMailBusy(false);
    }
  };

  const handleTracker = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackerEmail) return;
    setTrackerBusy(true);
    try {
      await submitEnforcementTracker(trackerEmail);
      setTrackerSent(true);
      setTrackerEmail('');
    } catch (err) {
      console.error('tracker signup failed', err);
    } finally {
      setTrackerBusy(false);
    }
  };

  return (
    <footer className="site-footer">
      <div className="footer-inner">
        <div className="footer-grid">
          <div>
            <div className="footer-mark mark">
              <span>we</span>
              <Slash />
              <span>dpo</span>
            </div>
            <div className="footer-links">
              <a href="https://scanner.wedpo.com" target="_blank" rel="noopener noreferrer">Automated Compliance Scanner</a>
              <Link to="/#diagnostic">Do I need a DPO?</Link>
              <Link to="/#contact">Contact us</Link>
              <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
              <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
            </div>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">Stay informed</div>
            <p>Subscribe to our mailing list for compliance news, regulatory updates and WeDPO announcements.</p>
            <form onSubmit={handleMailing} className="footer-form">
              <input
                type="email"
                required
                placeholder="your@email.com"
                value={mailEmail}
                onChange={(e) => setMailEmail(e.target.value)}
                disabled={mailSent || mailBusy}
                className="footer-input"
              />
              <button
                type="submit"
                disabled={mailSent || mailBusy}
                className="footer-btn"
              >
                {mailSent ? 'Subscribed ✓' : mailBusy ? '…' : 'Subscribe'}
              </button>
            </form>
            {mailSent && <div className="footer-success">Subscribed successfully.</div>}
          </div>
          <div className="footer-col">
            <div className="footer-col-title">GDPR Enforcement Tracker</div>
            <p>Get alerts on GDPR enforcement actions, fines and regulatory decisions relevant to your sector.</p>
            <form onSubmit={handleTracker} className="footer-form">
              <input
                type="email"
                required
                placeholder="your@email.com"
                value={trackerEmail}
                onChange={(e) => setTrackerEmail(e.target.value)}
                disabled={trackerSent || trackerBusy}
                className="footer-input"
              />
              <button
                type="submit"
                disabled={trackerSent || trackerBusy}
                className="footer-btn footer-btn--gradient"
              >
                {trackerSent ? 'Signed up ✓' : trackerBusy ? '…' : 'Sign up'}
              </button>
            </form>
            {trackerSent && <div className="footer-success">You are now signed up for enforcement alerts.</div>}
          </div>
        </div>
        <div className="footer-bottom">
          <div>© {new Date().getFullYear()} WeDPO and Entero Strategies. All rights reserved.</div>
          <div>Powered by Entero Strategies Ltd. · Compliance experts since 2013</div>
        </div>
      </div>
    </footer>
  );
}

/* ─── CTA Band (shared between main + packages page) ───── */

export function CtaBand({ onProposal }: { onProposal: () => void }) {
  return (
    <div className="cta-band" id="contact">
      <div style={{ maxWidth: '660px', margin: '0 auto' }}>
        <div
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--g3)',
            marginBottom: '1rem',
          }}
        >
          Ready to start?
        </div>
        <h2>Not sure which package fits?</h2>
        <p>Leave your details and one of our experts will help you find the right solution for your organisation.</p>
        <div className="cta-band-actions">
          <button
            type="button"
            onClick={onProposal}
            className="btn-primary"
            style={{ fontSize: '0.95rem', padding: '0.9rem 2.25rem' }}
          >
            Get a tailored proposal →
          </button>
          <a
            href="mailto:info@wedpo.com"
            className="btn-outline"
            style={{ fontSize: '0.95rem', padding: '0.9rem 2.25rem' }}
          >
            Email us directly
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─── Modal shell ──────────────────────────────────────── */

export function ModalShell({
  title,
  onClose,
  width = 760,
  noPadding = false,
  children,
}: {
  title: string;
  onClose: () => void;
  width?: number;
  noPadding?: boolean;
  children: React.ReactNode;
}) {
  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  // Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box" style={{ maxWidth: `${width}px` }}>
        <div className="modal-head">
          <div className="modal-title">{title}</div>
          <button type="button" onClick={onClose} className="modal-close" aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ padding: noPadding ? 0 : '2rem' }}>{children}</div>
      </div>
    </div>
  );
}

/* ─── Proposal modal ──────────────────────────────────── */

export function ProposalModal({
  onClose,
  initialPackage,
  onClearPackage,
}: {
  onClose: () => void;
  initialPackage: PackageName | '';
  onClearPackage: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedSolutions, setSelectedSolutions] = useState<string[]>([]);
  const [companyName, setCompanyName] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [country, setCountry] = useState('');
  const [employees, setEmployees] = useState('');
  const [businessModel, setBusinessModel] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const loadedAt = useRef<number>(Date.now());

  const toggleSolution = (s: string) => {
    setSelectedSolutions((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (honeypot) {
      setDone(true);
      return;
    }
    if (Date.now() - loadedAt.current < 3000) {
      setErr('Please review your details before submitting.');
      return;
    }
    setBusy(true);
    // Auto-prepend https:// when visitors type a bare domain like
    // "www.entero.io" or "entero.io" - most do, and the type="url" browser
    // validator was rejecting these despite being clearly a URL.
    const normalisedUrl = companyUrl.trim()
      ? /^https?:\/\//i.test(companyUrl.trim())
        ? companyUrl.trim()
        : `https://${companyUrl.trim()}`
      : '';
    const payload: ProposalSubmission = {
      firstName, lastName, email, phone,
      selectedPackage: initialPackage || '',
      selectedSolutions,
      companyName, companyUrl: normalisedUrl, country, employees, businessModel,
      marketingConsent,
    };
    try {
      await submitProposal(payload);
      setDone(true);
    } catch (e: unknown) {
      console.error('proposal submit failed', e);
      setErr('Submission could not be completed. Please try again or email info@wedpo.com.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Get a Proposal" onClose={onClose} width={620} noPadding>
      <div style={{ background: 'var(--paper)', padding: '2rem 2.25rem 2.25rem' }}>
        {done ? (
          <div style={{ padding: '3rem 0', textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
            <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: '1.5rem', fontWeight: 500, color: 'var(--ink)', letterSpacing: '-0.022em', marginBottom: '0.5rem' }}>Thank you!</div>
            <p style={{ fontSize: '0.9rem', color: 'var(--ink-60)' }}>We will be in touch shortly.</p>
            <button
              type="button"
              onClick={onClose}
              style={{ marginTop: '1.5rem', background: 'var(--ink)', color: 'var(--paper)', padding: '0.75rem 2rem', borderRadius: '999px', fontWeight: 500, fontSize: '0.88rem', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {initialPackage && (
              <div
                style={{
                  marginBottom: '1.75rem',
                  padding: '0.85rem 1.25rem',
                  background: 'var(--canvas)',
                  border: '1px solid var(--ink-20)',
                  borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
                }}
              >
                <div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-60)', marginBottom: '0.25rem' }}>
                    Your request
                  </div>
                  <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: '1.05rem', fontWeight: 500, color: 'var(--ink)', letterSpacing: '-0.015em' }}>
                    {initialPackage} package
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClearPackage}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink)', fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 500, padding: 0 }}
                >
                  Change
                </button>
              </div>
            )}

            <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: '1.1rem', fontWeight: 500, color: 'var(--ink)', textAlign: 'center', marginBottom: '1.5rem', letterSpacing: '-0.015em' }}>
              Your Contact Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '0.85rem' }}>
              <input className="form-input" type="text" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              <input className="form-input" type="text" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '1.75rem' }}>
              <input className="form-input" type="email" required placeholder="E-mail Address *" value={email} onChange={(e) => setEmail(e.target.value)} />
              <input className="form-input" type="tel" placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>

            <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: '1.1rem', fontWeight: 500, color: 'var(--ink)', textAlign: 'center', marginBottom: '1.25rem', letterSpacing: '-0.015em' }}>
              Selected Solutions &amp; Modules
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', marginBottom: '1.75rem', padding: '0 0.5rem' }}>
              {SOLUTION_OPTIONS.map((s) => (
                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.88rem', color: 'var(--ink-80)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    style={{ width: '17px', height: '17px', accentColor: '#7B3FE4', cursor: 'pointer', flexShrink: 0 }}
                    checked={selectedSolutions.includes(s)}
                    onChange={() => toggleSolution(s)}
                  />
                  {s}
                </label>
              ))}
            </div>

            <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: '1.1rem', fontWeight: 500, color: 'var(--ink)', textAlign: 'center', marginBottom: '1.25rem', letterSpacing: '-0.015em' }}>
              Company Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '0.85rem' }}>
              <input className="form-input" type="text" required placeholder="Company Name *" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
              <input className="form-input" type="text" required placeholder="Company URL *" value={companyUrl} onChange={(e) => setCompanyUrl(e.target.value)} inputMode="url" autoCapitalize="none" spellCheck={false} />
            </div>
            <div style={{ marginBottom: '0.85rem' }}>
              <input className="form-input" type="text" placeholder="Country of incorporation" value={country} onChange={(e) => setCountry(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '1.75rem' }}>
              <select className="form-input" value={employees} data-empty={employees ? 'false' : 'true'} onChange={(e) => setEmployees(e.target.value)}>
                <option value="">Number of employees</option>
                <option>1-10</option><option>11-50</option><option>51-250</option><option>251-1000</option><option>1000+</option>
              </select>
              <select className="form-input" value={businessModel} data-empty={businessModel ? 'false' : 'true'} onChange={(e) => setBusinessModel(e.target.value)}>
                <option value="">Business Model</option>
                <option>B2B SaaS</option><option>B2C</option><option>Enterprise</option><option>Public sector</option><option>Other</option>
              </select>
            </div>

            <p style={{ fontSize: '0.82rem', color: 'var(--ink-80)', marginBottom: '0.4rem', lineHeight: 1.6 }}>
              By submitting the form, you consent to the processing of your data in accordance with our{' '}
              <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--g1)', textDecoration: 'underline' }}>
                Privacy Policy
              </Link>.
            </p>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', fontSize: '0.82rem', color: 'var(--ink-80)', cursor: 'pointer', marginBottom: '1.5rem', lineHeight: 1.6, marginTop: '0.85rem' }}>
              <input
                type="checkbox"
                style={{ width: '17px', height: '17px', accentColor: '#7B3FE4', flexShrink: 0, marginTop: '2px', cursor: 'pointer' }}
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
              />
              I would like to receive WeDPO updates, notes and compliance news, new products, features and events. I can unsubscribe at any time.
            </label>

            <input
              type="text"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              aria-hidden="true"
            />

            {err && (
              <div style={{ marginTop: '0.5rem', color: 'var(--flag)', fontSize: '0.82rem' }}>{err}</div>
            )}

            <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
              <button
                type="submit"
                disabled={busy}
                style={{
                  background: 'var(--ink)', color: 'var(--paper)',
                  padding: '0.9rem 2.5rem', borderRadius: '999px',
                  fontWeight: 500, fontSize: '0.9rem', border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  minWidth: '220px',
                  opacity: busy ? 0.6 : 1,
                  transition: 'opacity 0.18s, transform 0.18s',
                }}
              >
                {busy ? 'Submitting…' : 'Get a Proposal'}
              </button>
            </div>
          </form>
        )}
      </div>
    </ModalShell>
  );
}

/* ─── ROPA modal ──────────────────────────────────────── */

export function RopaModal({ onClose, onProposal }: { onClose: () => void; onProposal: () => void }) {
  return (
    <ModalShell title="ROPA Management" onClose={onClose} width={760} noPadding>
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📂</div>
        <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: '1.35rem', color: 'var(--ink)', fontWeight: 500, marginBottom: '0.75rem', letterSpacing: '-0.022em' }}>
          Automated ROPA Management
        </div>
        <p style={{ color: 'var(--ink-80)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '1.5rem', maxWidth: '52ch', margin: '0 auto 1.5rem' }}>
          Maintain your Record of Processing Activities automatically. Our tool maps data flows, tracks changes over time and keeps your ROPA audit-ready without manual effort or spreadsheets.
        </p>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--g1)', padding: '0.5rem 1rem', borderRadius: '100px', border: '1px solid var(--ink-20)', background: 'var(--canvas)', marginBottom: '1.5rem' }}>
          Available on Advanced and Custom packages
        </div>
        <div>
          <button
            type="button"
            onClick={() => { onClose(); onProposal(); }}
            style={{ background: 'var(--ink)', color: 'var(--paper)', padding: '0.7rem 1.6rem', borderRadius: '999px', fontSize: '0.88rem', fontWeight: 500, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Get a proposal
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ─── Hook: open the proposal modal automatically when arriving with ?open=proposal&pkg=… ─── */
export function useOpenModalFromQuery(setModal: (k: ModalKind) => void, setSelectedPackage: (p: PackageName | '') => void) {
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const open = params.get('open');
    if (open === 'proposal') {
      const pkg = params.get('pkg') as PackageName | null;
      if (pkg && (pkg === 'Basic' || pkg === 'Advanced' || pkg === 'Custom')) {
        setSelectedPackage(pkg);
      }
      setModal('proposal');
      // Strip the query string so refresh doesn't reopen.
      navigate(window.location.pathname + window.location.hash, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/* Re-exported for the routes that need them. */
export { Link };
